import { useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { AlertCircle, HardHat, Loader2 } from "lucide-react";
import { APP_NAME, APP_TAGLINE } from "@crewledger/shared/constants";
import { useAuth } from "@/features/auth/auth-provider";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { InfoBanner } from "@/components/design/info-banner";

export function LoginPage() {
  const {
    ready,
    authenticated,
    serverAuthenticated,
    redirectTo,
    sessionError,
    login,
  } = useAuth();

  useEffect(() => {
    document.title = `Sign in — ${APP_NAME}`;
  }, []);

  if (ready && authenticated && serverAuthenticated && redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  const checkingSession = authenticated && !ready;
  const showSessionFailed =
    ready && authenticated && (!serverAuthenticated || Boolean(sessionError));

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-muted/30 p-6">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <HardHat className="h-6 w-6" />
          </div>
          <CardTitle>{APP_NAME}</CardTitle>
          <CardDescription>{APP_TAGLINE}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {checkingSession ? (
            <div className="flex flex-col items-center gap-3 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden />
              <p>Checking your account…</p>
            </div>
          ) : null}

          {!checkingSession && showSessionFailed ? (
            <InfoBanner icon={AlertCircle} title="Sign-in could not be completed" variant="error">
              {sessionError ?? "The server could not verify your session. Please try again."}
            </InfoBanner>
          ) : null}

          {!checkingSession && !authenticated ? (
            <>
              <p className="text-center text-sm text-muted-foreground">
                Sign in with your work email. We&apos;ll send you a one-time code — no password or
                wallet required.
              </p>
              <Button className="w-full" disabled={!ready} onClick={login}>
                Continue with email
              </Button>
            </>
          ) : null}

          <p className="text-center text-sm text-muted-foreground">
            Want to explore first?{" "}
            <Link to="/demo" className="font-medium text-orange-construction hover:underline">
              Try the interactive demo
            </Link>
            {" · "}
            <Link to="/" className="font-medium text-navy-deep hover:underline">
              Back to home
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
